// src/components/LoyaltyBalanceLookup.js
// TKT-2026-106724, D4: a shopper checks their loyalty points balance at the
// kiosk by typing the email or phone on their account.
//
// The kiosk is a public screen, so this is built around the NEXT shopper:
//   - it is drawn only when the store's program says the kiosk shows balances;
//     anything else, including a failed read, draws nothing at all;
//   - it shows the balance in the store's own point names and nothing about the
//     account (the backend passes on nothing else to show);
//   - the typed email or phone leaves the field the moment it is looked up, and
//     the whole screen wipes itself after a short idle, result or not.
//
// A lookup needs a PAIRED kiosk (KioskLoyaltyFinish-1017, Mikah 2026-10-17:
// "Each kiosk gets a device token at setup; a lookup without one is refused").
// Until it is paired, the screen shows staff how: make a code in SortSwift
// (Integrations, Kiosk Settings, Pair a kiosk) and type it here. The token the
// code buys is kept in this browser per store and sent with every lookup. A
// kiosk removed in Kiosk Settings answers not_paired, forgets its token and
// asks again.
import React, { useCallback, useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import {
    Box,
    Button,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    TextField,
    Typography,
} from '@mui/material';
import { Loyalty as LoyaltyIcon } from '@mui/icons-material';
import { fetchLoyaltyProgram, lookupLoyaltyBalance, pairKiosk } from '../api/products';
import { useKioskTheme } from '../contexts/KioskThemeContext';
import KioskKeyboard from './KioskKeyboard';

// How long a looked-up result stays on screen, and how long the screen waits
// on a shopper who stopped typing, before it closes and forgets everything.
export const RESULT_CLEAR_MS = 15 * 1000;
export const IDLE_CLEAR_MS = 45 * 1000;

const PROMPT = 'Enter the email address or phone number on your account.';
const SHOWN_STATUSES = new Set(['found', 'multiple', 'not_found', 'invalid', 'rate_limited', 'unavailable']);

const PAIR_STEPS = 'Staff: open SortSwift, go to Integrations, then Kiosk Settings, and press Pair a kiosk. Type the code it shows here.';
const PAIR_MESSAGES = {
    unpaired: 'Points lookup is not set up on this kiosk yet. Please ask at the counter.',
    removed: 'This kiosk is no longer paired for points lookups. Please ask at the counter.',
    invalid_code: 'That code did not work. A code works once and lasts 15 minutes. Make a new one in Kiosk Settings.',
    rate_limited: 'Too many tries from this kiosk. Please wait a minute and try again.',
    unavailable: 'Pairing is not available right now. Please try again in a minute.',
};

/** Where this browser keeps the kiosk's device token, one per store. */
export function deviceTokenKey(storeName) {
    return `kiosk-device-token-${storeName}`;
}

function readDeviceToken(storeName) {
    try {
        return localStorage.getItem(deviceTokenKey(storeName)) || '';
    } catch {
        return '';
    }
}

function writeDeviceToken(storeName, token) {
    try {
        if (token) localStorage.setItem(deviceTokenKey(storeName), token);
        else localStorage.removeItem(deviceTokenKey(storeName));
    } catch { /* storage blocked: the kiosk asks to be paired again next time */ }
}

function resultMessage(result) {
    switch (result.status) {
        case 'found': {
            const unit = result.balance === 1 ? result.pointName : result.pointNamePlural;
            return `You have ${result.balance.toLocaleString()} ${unit}.`;
        }
        case 'multiple':
            return 'We could not look that up here. Please ask at the counter.';
        case 'not_found':
            return 'We could not find an account with that email or phone number.';
        case 'invalid':
            return PROMPT;
        case 'rate_limited':
            return 'Too many lookups from this kiosk. Please try again in a minute.';
        default:
            return 'Points lookup is not available right now. Please ask at the counter.';
    }
}

export default function LoyaltyBalanceLookup({ storeName }) {
    const { themeOverrides } = useKioskTheme();
    const onScreenKeyboard = themeOverrides?.onScreenKeyboardEnabled === true;

    const [program, setProgram] = useState(null);
    const [open, setOpen] = useState(false);
    const [identifier, setIdentifier] = useState('');
    const [result, setResult] = useState(null);
    const [busy, setBusy] = useState(false);
    const [shift, setShift] = useState(false);
    const [symbols, setSymbols] = useState(false);
    const [deviceToken, setDeviceToken] = useState(() => readDeviceToken(storeName));
    const [pairCode, setPairCode] = useState('');
    // 'unpaired' | 'removed' | 'invalid_code' | 'rate_limited' | 'unavailable' | 'paired'
    const [pairNote, setPairNote] = useState('unpaired');
    // Bumped on every clear, so an answer that lands after the screen was wiped
    // is dropped instead of being shown to whoever is standing there now.
    const requestRef = useRef(0);

    useEffect(() => {
        setDeviceToken(readDeviceToken(storeName));
        setPairNote('unpaired');
    }, [storeName]);

    useEffect(() => {
        let live = true;
        setProgram(null);
        Promise.resolve()
            .then(() => fetchLoyaltyProgram(storeName))
            .then((res) => {
                const data = res && res.data;
                if (live && data && data.enabled === true) setProgram(data);
            })
            .catch(() => { /* no program read, no screen */ });
        return () => { live = false; };
    }, [storeName]);

    const clearAll = useCallback(() => {
        requestRef.current += 1;
        setOpen(false);
        setIdentifier('');
        setResult(null);
        setBusy(false);
        setShift(false);
        setSymbols(false);
        setPairCode('');
        setPairNote((note) => (note === 'removed' ? note : 'unpaired'));
    }, []);

    useEffect(() => () => { requestRef.current += 1; }, []);

    // Re-armed by every keystroke and every answer, so it measures idle time.
    useEffect(() => {
        if (!open) return undefined;
        const timer = setTimeout(clearAll, result ? RESULT_CLEAR_MS : IDLE_CLEAR_MS);
        return () => clearTimeout(timer);
    }, [open, identifier, result, pairCode, pairNote, clearAll]);

    const edit = useCallback((next) => {
        setIdentifier(next);
        setResult(null);
    }, []);

    const submit = useCallback(async () => {
        const typed = identifier.trim();
        if (!typed || busy) return;
        const request = ++requestRef.current;
        setBusy(true);
        setIdentifier('');
        let answer;
        try {
            const res = await lookupLoyaltyBalance(storeName, typed, deviceToken);
            answer = res && res.data;
        } catch {
            answer = null;
        }
        if (request !== requestRef.current) return;
        setBusy(false);
        const status = answer && answer.status;
        if (status === 'disabled') {
            clearAll();
            setProgram(null);
            return;
        }
        if (status === 'not_paired') {
            writeDeviceToken(storeName, '');
            setDeviceToken('');
            setPairNote('removed');
            setResult(null);
            return;
        }
        if (status === 'invalid') setIdentifier(typed);
        setResult(SHOWN_STATUSES.has(status) && (status !== 'found' || Number.isSafeInteger(answer.balance))
            ? answer
            : { status: 'unavailable' });
    }, [identifier, busy, storeName, deviceToken, clearAll]);

    const pair = useCallback(async () => {
        const code = pairCode.trim().toUpperCase();
        if (!code || busy) return;
        const request = ++requestRef.current;
        setBusy(true);
        let answer;
        try {
            const res = await pairKiosk(storeName, code);
            answer = res && res.data;
        } catch {
            answer = null;
        }
        if (request !== requestRef.current) return;
        setBusy(false);
        const status = answer && answer.status;
        if (status === 'paired' && typeof answer.deviceToken === 'string' && answer.deviceToken) {
            writeDeviceToken(storeName, answer.deviceToken);
            setDeviceToken(answer.deviceToken);
            setPairCode('');
            setPairNote('paired');
            return;
        }
        setPairNote(status === 'invalid_code' || status === 'rate_limited' ? status : 'unavailable');
    }, [pairCode, busy, storeName]);

    if (!program) return null;

    return (
        <>
            <Button
                color='inherit'
                onClick={() => { clearAll(); setOpen(true); }}
                startIcon={<LoyaltyIcon />}
                sx={{ minHeight: 44, textTransform: 'none', fontWeight: 600, whiteSpace: 'nowrap' }}
            >
                {`My ${program.pointNamePlural}`}
            </Button>

            <Dialog open={open} onClose={clearAll} fullWidth maxWidth='sm'>
                <DialogTitle>{program.programName}</DialogTitle>
                {!deviceToken ? (
                    <>
                        <DialogContent>
                            <Typography role='status' sx={{ fontWeight: 600 }}>
                                {PAIR_MESSAGES[pairNote === 'removed' ? 'removed' : 'unpaired']}
                            </Typography>
                            <Typography variant='body2' sx={{ mt: 1 }}>{PAIR_STEPS}</Typography>
                            <Box
                                component='form'
                                noValidate
                                autoComplete='off'
                                onSubmit={(e) => { e.preventDefault(); pair(); }}
                                sx={{ pt: 2 }}
                            >
                                <TextField
                                    label='Pairing code'
                                    fullWidth
                                    value={pairCode}
                                    onChange={(e) => { setPairCode(e.target.value.toUpperCase()); }}
                                    disabled={busy}
                                    inputProps={{
                                        maxLength: 9,
                                        autoComplete: 'off',
                                        autoCapitalize: 'characters',
                                        spellCheck: false,
                                        ...(onScreenKeyboard ? { inputMode: 'none' } : {}),
                                    }}
                                />
                                {onScreenKeyboard && (
                                    <KioskKeyboard
                                        layout='text'
                                        onKey={(ch) => setPairCode((c) => (c + ch).toUpperCase().slice(0, 9))}
                                        onBackspace={() => setPairCode((c) => c.slice(0, -1))}
                                        onDone={pair}
                                        shift={shift}
                                        onToggleShift={() => setShift((s) => !s)}
                                        symbols={symbols}
                                        onToggleSymbols={() => setSymbols((s) => !s)}
                                    />
                                )}
                            </Box>
                            {PAIR_MESSAGES[pairNote] && pairNote !== 'unpaired' && pairNote !== 'removed' && (
                                <Typography role='alert' sx={{ mt: 2 }}>{PAIR_MESSAGES[pairNote]}</Typography>
                            )}
                        </DialogContent>
                        <DialogActions>
                            <Button onClick={clearAll}>Done</Button>
                            <Button variant='contained' onClick={pair} disabled={busy || !pairCode.trim()}>
                                Pair this kiosk
                            </Button>
                        </DialogActions>
                    </>
                ) : (
                <>
                <DialogContent>
                    {pairNote === 'paired' && (
                        <Typography sx={{ mb: 1 }}>This kiosk is paired. Shoppers can look up their points here now.</Typography>
                    )}
                    <Box
                        component='form'
                        noValidate
                        autoComplete='off'
                        onSubmit={(e) => { e.preventDefault(); submit(); }}
                        sx={{ pt: 1 }}
                    >
                        <TextField
                            label='Email or phone'
                            helperText={PROMPT}
                            fullWidth
                            autoFocus={!onScreenKeyboard}
                            value={identifier}
                            onChange={(e) => edit(e.target.value)}
                            disabled={busy}
                            inputProps={{
                                maxLength: 254,
                                autoComplete: 'off',
                                autoCapitalize: 'none',
                                spellCheck: false,
                                ...(onScreenKeyboard ? { inputMode: 'none' } : { inputMode: 'email' }),
                            }}
                        />
                        {onScreenKeyboard && (
                            <KioskKeyboard
                                layout='email'
                                onKey={(ch) => edit(identifier + ch)}
                                onBackspace={() => edit(identifier.slice(0, -1))}
                                onDone={submit}
                                shift={shift}
                                onToggleShift={() => setShift((s) => !s)}
                                symbols={symbols}
                                onToggleSymbols={() => setSymbols((s) => !s)}
                            />
                        )}
                    </Box>
                    {result && (
                        <Typography
                            role='status'
                            variant={result.status === 'found' ? 'h5' : 'body1'}
                            sx={{ mt: 2, fontWeight: result.status === 'found' ? 700 : 500 }}
                        >
                            {resultMessage(result)}
                        </Typography>
                    )}
                </DialogContent>
                <DialogActions>
                    <Button onClick={clearAll}>Done</Button>
                    <Button variant='contained' onClick={submit} disabled={busy || !identifier.trim()}>
                        Look up
                    </Button>
                </DialogActions>
                </>
                )}
            </Dialog>
        </>
    );
}

LoyaltyBalanceLookup.propTypes = {
    storeName: PropTypes.string.isRequired,
};
